import { CalendarDays, MapPinned, Route, Timer } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import { VisitTable } from '../components/VisitTable';
import { WarningList } from '../components/ImportProgress';
import { PlaceNameResolver } from '../components/PlaceNameResolver';
import { AnalysisInterpretation } from '../components/AnalysisInterpretation';
import { JourneyNarrative } from '../components/JourneyNarrative';
import { formatKm } from '../domain/distance';
import type { AnalysisResult } from '../domain/models';
import { rankVisits } from '../domain/aggregators';

export function DashboardPage({ result, onAnalysisChange }: { result: AnalysisResult; onAnalysisChange: (result: AnalysisResult) => void }) {
  const ranking = rankVisits(result.visits, 8);
  const weekdays = buildWeekdayStats(result);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {result.stats.dateStart ?? '-'} から {result.stats.dateEnd ?? '-'} までの解析結果です。
          </p>
        </div>
      </header>

      <WarningList warnings={result.warnings} />

      <JourneyNarrative result={result} />
      <PlaceNameResolver result={result} onResolved={onAnalysisChange} />

      <section className="grid four">
        <StatCard label="総移動距離" value={`${formatKm(result.stats.totalDistanceMeters)} km`} foot={<Route size={14} aria-hidden="true" />} />
        <StatCard label="総滞在時間" value={`${Math.round(result.stats.totalStayMinutes / 60)} h`} foot={<Timer size={14} aria-hidden="true" />} />
        <StatCard label="訪問候補" value={result.stats.visitCount.toLocaleString('ja-JP')} foot={<MapPinned size={14} aria-hidden="true" />} />
        <StatCard label="日次集計" value={result.dailySummaries.length.toLocaleString('ja-JP')} foot={<CalendarDays size={14} aria-hidden="true" />} />
      </section>

      <AnalysisInterpretation result={result} />

      <section className="grid two">
        <div className="section">
          <div>
            <h2 className="section-title">根拠データ: よく滞在した場所</h2>
            <p className="section-note">ストーリーの裏付けです。半径 250m 以内の滞在候補を同一地点としてまとめています。</p>
          </div>
          <VisitTable visits={ranking} />
        </div>

        <div className="section">
          <div>
            <h2 className="section-title">根拠データ: 曜日別外出傾向</h2>
            <p className="section-note">曜日ごとの偏りを見るための補助データです。</p>
          </div>
          <div className="surface table-wrap">
            <table>
              <thead>
                <tr>
                  <th>曜日</th>
                  <th>日数</th>
                  <th>平均距離</th>
                  <th>平均訪問数</th>
                </tr>
              </thead>
              <tbody>
                {weekdays.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.days}</td>
                    <td>{formatKm(row.distanceMeters / Math.max(1, row.days))} km</td>
                    <td>{(row.visitCount / Math.max(1, row.days)).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function buildWeekdayStats(result: AnalysisResult) {
  const labels = ['日', '月', '火', '水', '木', '金', '土'];
  const rows = labels.map((label) => ({ label, days: 0, distanceMeters: 0, visitCount: 0 }));
  for (const day of result.dailySummaries) {
    const index = new Date(`${day.date}T00:00:00`).getDay();
    rows[index].days += 1;
    rows[index].distanceMeters += day.distanceMeters;
    rows[index].visitCount += day.visitCount;
  }
  return rows;
}
